import React, { useState, useEffect, useMemo } from 'react';
import RecentActivity from './Overview/RecentActivity';
import { CalendarDaysIcon, CreditCardIcon, UserCircleIcon } from '@heroicons/react/20/solid';

// Helper function to generate consistent, varied colors for months
const getMonthColor = (monthStr) => {
  const [year, monthNum] = monthStr.split('-');
  const monthIndex = parseInt(monthNum, 10);
  const yearNum = parseInt(year, 10);
  
  // Use prime multipliers to spread adjacent months far apart in color space
  const hue = ((monthIndex * 83) + (yearNum * 47)) % 360;
  
  // Make colors more pastel by reducing saturation and increasing lightness
  const saturation = 60 + (monthIndex * 3) % 15; // Lower saturation for pastel look
  const lightness = 75 + (monthIndex * 5) % 10;  // Higher lightness for pastel look
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export default function Overview() {
  const [constructionData, setConstructionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load construction data including project start date
  useEffect(() => {
    const fetchConstructionData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { fetchConstructionData } = await import('../utils/api');
        const data = await fetchConstructionData();
        
        setConstructionData(data);
      } catch (err) {
        setError('Failed to load construction data');
        console.error('Error loading construction data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchConstructionData();
  }, []);
  
  // Calculate project statistics and payment/contract data by month
  const { projectStats, paymentsData, contractsData, totalBudget } = useMemo(() => {
    if (!constructionData || !constructionData.scope_items || constructionData.scope_items.length === 0) {
      return { projectStats: [], paymentsData: [], contractsData: [], totalBudget: 0 };
    }
    
    const scopeItems = constructionData.scope_items;
    
    // Calculate total budget (excluding null budgets)
    const validBudgets = scopeItems.filter(item => item.budget !== null && item.budget !== undefined);
    const totalBudget = validBudgets.reduce((sum, item) => sum + item.budget, 0);
    
    // Count items by status
    const statusCounts = {};
    scopeItems.forEach(item => {
      const status = item.status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    
    // Calculate completion percentage
    const totalItems = scopeItems.length;
    const itemsWithCompletion = scopeItems.filter(item => 
      item.percentage_complete !== null && 
      item.percentage_complete !== undefined
    );
    const completionSum = itemsWithCompletion.reduce(
      (sum, item) => sum + item.percentage_complete, 
      0
    );
    const averageCompletion = itemsWithCompletion.length > 0 
      ? Math.round(completionSum / itemsWithCompletion.length) 
      : 0;
    
    // Collect payments by month
    const paymentsByMonth = {};
    let totalPayments = 0;
    
    scopeItems.forEach(item => {
      if (item.payments && Array.isArray(item.payments)) {
        item.payments.forEach(payment => {
          if (payment.date && payment.amount) {
            const date = new Date(payment.date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            
            if (!paymentsByMonth[monthKey]) {
              paymentsByMonth[monthKey] = {
                month: monthKey,
                monthDisplay: new Intl.DateTimeFormat('en-US', { 
                  year: 'numeric', 
                  month: 'short' 
                }).format(date),
                amount: 0,
                details: []
              };
            }
            
            paymentsByMonth[monthKey].amount += payment.amount;
            paymentsByMonth[monthKey].details.push({
              scope: item.scope,
              short_name: item.short_name,
              subcontractor: item.subcontractor || 'Unknown',
              amount: payment.amount,
              description: payment.description || '',
              date: payment.date
            });
            
            totalPayments += payment.amount;
          }
        });
      }
    });
    
    // Convert to sorted array
    const paymentsData = Object.values(paymentsByMonth).sort((a, b) => a.month.localeCompare(b.month));
    const paymentPercentage = totalBudget > 0 ? (totalPayments / totalBudget) * 100 : 0;
    
    // Calculate running total for the payment segments
    let runningPaymentTotal = 0;
    paymentsData.forEach(month => {
      runningPaymentTotal += month.amount;
      month.runningTotal = runningPaymentTotal;
      month.runningPercentage = totalBudget > 0 ? (runningPaymentTotal / totalBudget) * 100 : 0;
      month.segmentPercentage = totalBudget > 0 ? (month.amount / totalBudget) * 100 : 0;
    });
    
    // Collect contracts by month
    const contractsByMonth = {};
    let totalContractsValue = 0;
    
    scopeItems.forEach(item => {
      if (item.contracts && Array.isArray(item.contracts)) {
        item.contracts.forEach(contract => {
          if (contract.date && contract.amount) {
            const date = new Date(contract.date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            
            if (!contractsByMonth[monthKey]) {
              contractsByMonth[monthKey] = {
                month: monthKey,
                monthDisplay: new Intl.DateTimeFormat('en-US', { 
                  year: 'numeric', 
                  month: 'short' 
                }).format(date),
                amount: 0,
                details: []
              };
            }
            
            contractsByMonth[monthKey].amount += contract.amount;
            contractsByMonth[monthKey].details.push({
              scope: item.scope,
              short_name: item.short_name,
              subcontractor: contract.subcontractor || item.subcontractor || 'Unknown',
              amount: contract.amount,
              description: contract.description || '',
              date: contract.date
            });
            
            totalContractsValue += contract.amount;
          }
        });
      }
    });
    
    // Convert to sorted array
    const contractsData = Object.values(contractsByMonth).sort((a, b) => a.month.localeCompare(b.month));
    const contractPercentage = totalBudget > 0 ? (totalContractsValue / totalBudget) * 100 : 0;
    
    // Calculate running total for the contract segments
    let runningContractTotal = 0;
    contractsData.forEach(month => {
      runningContractTotal += month.amount;
      month.runningTotal = runningContractTotal;
      month.runningPercentage = totalBudget > 0 ? (runningContractTotal / totalBudget) * 100 : 0;
      month.segmentPercentage = totalBudget > 0 ? (month.amount / totalBudget) * 100 : 0;
    });
    
    // Items in progress
    const inProgressCount = scopeItems.filter(item => 
      (item.status === 'On Going' || item.status === 'On Going') && 
      item.percentage_complete > 0 && 
      item.percentage_complete < 100
    ).length;
    
    const projectStats = [
      { 
        name: 'Total Budget', 
        stat: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0
        }).format(totalBudget),
        progress: [
          { 
            name: 'Payments', 
            value: totalPayments, 
            percentage: paymentPercentage,
            color: 'bg-green-500'
          },
          { 
            name: 'Contracts', 
            value: totalContractsValue, 
            percentage: contractPercentage,
            color: 'bg-blue-500'
          }
        ]
      },
      { 
        name: 'Project Completion', 
        stat: `${averageCompletion}%`,
        progress: null
      },
      { 
        name: 'Budget Items', 
        stat: totalItems.toString(),
        progress: null
      },
    ];
    
    return { 
      projectStats,
      paymentsData,
      contractsData,
      totalBudget,
      totalPayments,
      totalContractsValue
    };
  }, [constructionData]);
  
  // Get recent payments from all scope items
  const recentPayments = useMemo(() => {
    if (!constructionData || !constructionData.scope_items) {
      return [];
    }
    
    // Collect all payments from all scope items
    const allPayments = [];
    constructionData.scope_items.forEach(item => {
      if (item.payments && Array.isArray(item.payments) && item.payments.length > 0) {
        item.payments.forEach(payment => {
          allPayments.push({
            ...payment,
            scope: item.scope,
            short_name: item.short_name,
            subcontractor: item.subcontractor || 'Unknown'
          });
        });
      }
    });
    
    // Sort by date (most recent first)
    allPayments.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
    
    // Return the 4 most recent payments
    return allPayments.slice(0, 4);
  }, [constructionData]);
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  return (
    <div className="py-4">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Construction Overview</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Summary of the 13210 E Sunset Dr construction project.
          </p>
        </div>
      </div>
      
      {/* Project Stats */}
      <div className="mt-5">
        {loading ? (
          <div className="animate-pulse grid grid-cols-1 gap-5 sm:grid-cols-3">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            ))}
          </div>
        ) : (
          <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {projectStats.map((item) => (
              <div key={item.name} className="overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 py-5 shadow-sm sm:p-6">
                <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">{item.name}</dt>
                <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">{item.stat}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      
      {/* Progress Bars */}
      {!loading && (
        <div className="mt-8">
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg px-6 py-5">
            <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100 mb-4">Budget Progress</h3>
            
            <div className="space-y-8">
              {/* Helper function to generate consistent colors for months */}
              {(() => {
                // Create a map of all unique months across both datasets
                const allMonths = new Set();
                paymentsData.forEach(m => allMonths.add(m.month));
                contractsData.forEach(m => allMonths.add(m.month));
                
                // Sort months chronologically
                const sortedMonths = Array.from(allMonths).sort();
                
                // Create a color legend if there are months
                return sortedMonths.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {sortedMonths.map(month => {
                      const dataset = [...paymentsData, ...contractsData].find(m => m.month === month);
                      // Get color from helper function
                      const color = getMonthColor(month);
                      const displayMonth = dataset?.monthDisplay || month;
                      
                      return (
                        <div key={month} className="flex items-center gap-1">
                          <div 
                            style={{ 
                              backgroundColor: color,
                              width: '12px',
                              height: '12px',
                              borderRadius: '3px'
                            }} 
                          />
                          <span className="text-xs text-gray-600 dark:text-gray-300">{displayMonth}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : null;
              })()}
            
              {/* Payments progress */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Payments by Month
                  </div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-baseline gap-2">
                    <span>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0
                      }).format(projectStats[0]?.progress?.[0]?.value || 0)}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      of {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0
                      }).format(totalBudget)}
                      {' '}({projectStats[0]?.progress?.[0]?.percentage.toFixed(1)}%)
                    </span>
                    {totalBudget > 0 && (
                      <span className="text-green-600 dark:text-green-400 text-xs border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/30 rounded-full px-2 py-0.5">
                        Remaining: {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          maximumFractionDigits: 0
                        }).format(totalBudget - (projectStats[0]?.progress?.[0]?.value || 0))}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mt-2">
                  <div className="absolute inset-0 flex">
                    {paymentsData.map((month, index) => (
                      <div 
                        key={month.month}
                        className="relative group h-full"
                        style={{ 
                          width: `${month.segmentPercentage}%`,
                          minWidth: month.segmentPercentage > 0.5 ? '2px' : '0px'
                        }}
                        title={`${month.monthDisplay}: ${new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          maximumFractionDigits: 0
                        }).format(month.amount)}`}
                      >
                        <div 
                          style={{ 
                            height: '100%',
                            backgroundColor: getMonthColor(month.month),
                            borderRadius: `${index === 0 ? '9999px' : '0'} ${index === paymentsData.length - 1 ? '9999px' : '0'} ${index === paymentsData.length - 1 ? '9999px' : '0'} ${index === 0 ? '9999px' : '0'}`
                          }}
                        ></div>
                        
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                          <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                            <div className="font-semibold">{month.monthDisplay}</div>
                            <div>
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                maximumFractionDigits: 0
                              }).format(month.amount)}
                            </div>
                            {month.details.length > 0 && (
                              <div className="mt-1 border-t border-gray-600 pt-1">
                                {month.details.slice(0, 3).map((detail, i) => (
                                  <div key={i} className="text-xs py-0.5">
                                    {detail.scope}: {new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      maximumFractionDigits: 0
                                    }).format(detail.amount)}
                                  </div>
                                ))}
                                {month.details.length > 3 && (
                                  <div className="text-xs text-gray-300">
                                    +{month.details.length - 3} more...
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                            <div className="border-solid border-t-gray-800 border-t-8 border-x-transparent border-x-8 border-b-0"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Month labels */}
                {paymentsData.length > 0 && (
                  <div className="flex mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <div>{paymentsData[0].monthDisplay}</div>
                  </div>
                )}
              </div>
              
              {/* Contracts progress */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Contracts by Month
                  </div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-baseline gap-2">
                    <span>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0
                      }).format(projectStats[0]?.progress?.[1]?.value || 0)}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      of {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0
                      }).format(totalBudget)}
                      {' '}({projectStats[0]?.progress?.[1]?.percentage.toFixed(1)}%)
                    </span>
                    {totalBudget > 0 && (
                      <span className="text-blue-600 dark:text-blue-400 text-xs border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 rounded-full px-2 py-0.5">
                        Remaining: {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          maximumFractionDigits: 0
                        }).format(totalBudget - (projectStats[0]?.progress?.[1]?.value || 0))}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mt-2">
                  <div className="absolute inset-0 flex">
                    {contractsData.map((month, index) => (
                      <div 
                        key={month.month}
                        className="relative group h-full" 
                        style={{ 
                          width: `${contractsData.length === 1 ? 100 : 
                                   Math.min(
                                     // If total contracts exceed budget, scale proportionally
                                     projectStats[0]?.progress?.[1]?.percentage > 100 
                                       ? (month.amount / projectStats[0]?.progress?.[1]?.value) * 100 
                                       : month.segmentPercentage, 
                                     100
                                   )}%`,
                          minWidth: month.segmentPercentage > 0.5 ? '2px' : '0px'
                        }}
                        title={`${month.monthDisplay}: ${new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          maximumFractionDigits: 0
                        }).format(month.amount)}`}
                      >
                        <div 
                          style={{ 
                            height: '100%',
                            backgroundColor: getMonthColor(month.month),
                            borderRadius: `${index === 0 ? '9999px' : '0'} ${index === contractsData.length - 1 ? '9999px' : '0'} ${index === contractsData.length - 1 ? '9999px' : '0'} ${index === 0 ? '9999px' : '0'}`
                          }}
                        ></div>
                        
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                          <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                            <div className="font-semibold">{month.monthDisplay}</div>
                            <div>
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                maximumFractionDigits: 0
                              }).format(month.amount)}
                            </div>
                            {month.details.length > 0 && (
                              <div className="mt-1 border-t border-gray-600 pt-1">
                                {month.details.slice(0, 3).map((detail, i) => (
                                  <div key={i} className="text-xs py-0.5">
                                    {detail.scope}: {new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      maximumFractionDigits: 0
                                    }).format(detail.amount)}
                                  </div>
                                ))}
                                {month.details.length > 3 && (
                                  <div className="text-xs text-gray-300">
                                    +{month.details.length - 3} more...
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                            <div className="border-solid border-t-gray-800 border-t-8 border-x-transparent border-x-8 border-b-0"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Month labels */}
                {contractsData.length > 0 && (
                  <div className="flex mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <div>{contractsData[0].monthDisplay}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Recent Payments */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center mb-4">
          <div className="sm:flex-auto">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Payments</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Most recent payments to contractors
            </p>
          </div>
        </div>
        
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-40 bg-gray-100 dark:bg-gray-700 rounded-md"></div>
            ))}
          </div>
        ) : recentPayments.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 shadow sm:rounded-lg">
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No payments yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Payments will appear here when they are recorded.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
            {recentPayments.map((payment, idx) => (
              <div key={`${payment.scope}-${payment.number || idx}`} className="rounded-lg bg-white dark:bg-gray-800 shadow ring-1 ring-gray-900/5 dark:ring-gray-700/30">
                <dl className="flex flex-wrap">
                  <div className="flex-auto pt-6 pl-6">
                    <dt className="text-sm font-semibold text-gray-900 dark:text-gray-100">Payment Amount</dt>
                    <dd className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(payment.amount)}
                    </dd>
                  </div>
                  <div className="flex-none self-end px-6 pt-4">
                    <dt className="sr-only">Status</dt>
                    <dd className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/30 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400 ring-1 ring-green-600/20 dark:ring-green-500/30 ring-inset">
                      Paid
                    </dd>
                  </div>
                  <div className="mt-6 flex w-full flex-none gap-x-4 border-t border-gray-900/5 dark:border-gray-700 px-6 pt-6">
                    <dt className="flex-none">
                      <span className="sr-only">Contractor</span>
                      <UserCircleIcon aria-hidden="true" className="h-6 w-5 text-gray-400 dark:text-gray-500" />
                    </dt>
                    <dd className="text-sm font-medium text-gray-900 dark:text-gray-100">{payment.subcontractor}</dd>
                  </div>
                  <div className="mt-4 flex w-full flex-none gap-x-4 px-6">
                    <dt className="flex-none">
                      <span className="sr-only">Date</span>
                      <CalendarDaysIcon aria-hidden="true" className="h-6 w-5 text-gray-400 dark:text-gray-500" />
                    </dt>
                    <dd className="text-sm text-gray-500 dark:text-gray-400">
                      <time dateTime={payment.date}>{formatDate(payment.date)}</time>
                    </dd>
                  </div>
                  <div className="mt-4 flex w-full flex-none gap-x-4 px-6">
                    <dt className="flex-none">
                      <span className="sr-only">Description</span>
                      <CreditCardIcon aria-hidden="true" className="h-6 w-5 text-gray-400 dark:text-gray-500" />
                    </dt>
                    <dd className="text-sm text-gray-500 dark:text-gray-400">{payment.description}</dd>
                  </div>
                </dl>
                <div className="mt-6 border-t border-gray-900/5 dark:border-gray-700 px-6 py-6">
                  <a href={`/construction/budget/${encodeURIComponent(payment.short_name)}`} className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    View {payment.scope} <span aria-hidden="true">&rarr;</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Recent Activity */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center mb-4">
          <div className="sm:flex-auto">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Activity</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              View the latest updates and comments from across all budget items
            </p>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg px-4 py-5 sm:p-6">
          <RecentActivity limit={100} allowComments={true} />
        </div>
      </div>
    </div>
  );
}